// Org Settings page. Standalone /settings and /orgs/:slug/settings
// route. Renders the shared Usage + Limits + Provider-keys components
// inside the public-page chrome. M014 refactor: body content extracted
// into ProviderKeysPanel + UsageLimitsPanel so the in-project Settings
// tab can render the same surface without forking.

import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { SidebarShell } from "../layout/SidebarShell";
import { ApiError, extractRows, listOrgs } from "../api/client";
import { UsageLimitsPanel } from "./UsageLimitsPanel";
import { ProviderKeysPanel } from "./ProviderKeysPanel";

interface Org {
  org_id: number;
  slug: string;
  name: string;
}

function rowToOrg(row: unknown[]): Org | null {
  const org_id = typeof row[0] === "number" ? row[0] : Number(row[0]);
  const slug = typeof row[1] === "string" ? row[1] : "";
  const name = typeof row[2] === "string" ? row[2] : "";
  if (!org_id || !slug) return null;
  return { org_id, slug, name };
}

export function SettingsPage() {
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const navigate = useNavigate();

  const [org, setOrg] = useState<Org | null>(null);
  const [error, setError] = useState("");

  const resolveOrg = useCallback(async () => {
    const rows = extractRows(await listOrgs())
      .map(rowToOrg)
      .filter((o): o is Org => o !== null);
    // If the URL carries an org slug, use that. Otherwise auto-pick
    // the user's first org so /settings works without a detour.
    const match = orgSlug ? rows.find((o) => o.slug === orgSlug) ?? null : rows[0] ?? null;
    setOrg(match);
  }, [orgSlug]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        await resolveOrg();
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof ApiError
              ? err.message
              : "Could not load your orgs. Refresh and try again.",
          );
        }
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [resolveOrg]);

  return (
    <SidebarShell>
      <div className="mx-auto flex max-w-3xl flex-col gap-6 p-8 pt-16">
        <header className="flex items-baseline justify-between gap-3 border-b border-edge pb-4">
          <div>
            <h1 className="text-sm font-semibold text-primary">
              Settings{org ? ` · ${org.name}` : ""}
            </h1>
            <p className="text-xs text-muted">
              Provider keys, agent defaults, and org preferences.
            </p>
          </div>
          <button
            onClick={() => navigate("/home")}
            className="cursor-pointer text-xs text-muted transition-colors hover:text-primary"
          >
            Back to home
          </button>
        </header>

        {error && (
          <p className="text-xs text-danger" data-testid="settings-load-error">
            {error}
          </p>
        )}

        {!org && orgSlug && !error && (
          <p className="text-sm text-muted" data-testid="settings-org-missing">
            No org found for slug <span className="font-code">@{orgSlug}</span>.
            Check the URL.
          </p>
        )}

        {org && (
          <>
            <UsageLimitsPanel orgId={org.org_id} />
            <ProviderKeysPanel orgId={org.org_id} orgName={org.name} />
          </>
        )}
      </div>
    </SidebarShell>
  );
}
